/**
 * BidderEarnings.jsx — Bidder earnings & payout history dashboard.
 *
 * Displays:
 *  - Summary cards: Total Earned, Pending Bank Transfer, Total Deals, Platform Fee
 *  - Payout Profile status banner (setup CTA if not configured)
 *  - Razorpay simulation status banner (dummy account indicator)
 *  - Scrollable payout history table with type badge, reference ID, and status
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider,
  Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Typography,
} from '@mui/material';
import AccountBalanceIcon    from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CheckCircleIcon       from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon       from '@mui/icons-material/EmojiEvents';
import HourglassTopIcon      from '@mui/icons-material/HourglassTop';
import OpenInNewIcon         from '@mui/icons-material/OpenInNew';
import PaymentIcon           from '@mui/icons-material/Payment';
import SettingsIcon          from '@mui/icons-material/Settings';
import VerifiedIcon          from '@mui/icons-material/Verified';
import WarningAmberIcon      from '@mui/icons-material/WarningAmber';

import { apiClient } from '../../utils/auth';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n) {
  return Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const PAYOUT_TYPE_CONFIG = {
  FULL:         { label: 'Full Release',      color: 'success' },
  MILESTONE:    { label: 'Milestone',         color: 'info'    },
  DISPUTE_SPLIT:{ label: 'Dispute Split',     color: 'warning' },
};

// ── Summary Card ─────────────────────────────────────────────────────────────

function SummaryCard({ icon, label, value, color = '#818cf8', sublabel }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1, minWidth: 180, p: 2.5,
        background: 'linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(30,41,59,0.95) 100%)',
        border: `1px solid rgba(129,140,248,0.18)`,
        borderRadius: 3,
        transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: color,
          boxShadow: `0 8px 30px ${color}22`,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ color, display: 'flex' }}>{icon}</Box>
        <Typography variant="caption" color="text.secondary" fontWeight={500} letterSpacing={0.5}>
          {label.toUpperCase()}
        </Typography>
      </Box>
      <Typography variant="h5" fontWeight={700} sx={{ color, mb: 0.25 }}>
        Rs.{value}
      </Typography>
      {sublabel && (
        <Typography variant="caption" color="text.secondary">{sublabel}</Typography>
      )}
    </Paper>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function BidderEarnings() {
  const navigate = useNavigate();

  const [loading, setLoading]   = useState(true);
  const [error,   setError]     = useState('');
  const [data,    setData]      = useState(null);   // { summary, payout_profile, payouts }

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: res } = await apiClient.get('/api/v1/payment/earnings/');
      setData(res);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load earnings data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 700, mx: 'auto', mt: 8, p: 3 }}>
        <Alert severity="error" action={<Button size="small" onClick={load}>Retry</Button>}>{error}</Alert>
      </Box>
    );
  }

  const { summary = {}, payout_profile = {}, payouts = [] } = data || {};

  return (
    <Box
      sx={{
        maxWidth: 1100, mx: 'auto', mt: 4, p: { xs: 2, md: 3 },
        background: 'transparent',
      }}
    >
      {/* ── Page Header ─────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44, height: 44, borderRadius: '50%',
              background: 'linear-gradient(135deg, #818cf8, #6366f1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(99,102,241,0.4)',
            }}
          >
            <EmojiEventsIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              My Earnings
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Payout history &amp; Razorpay transfer tracking
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<SettingsIcon />}
          onClick={() => navigate('/account/payout-profile')}
          id="btn-go-payout-profile"
          sx={{ borderRadius: 20, textTransform: 'none' }}
        >
          Payout Profile
        </Button>
      </Box>

      {/* ── Payout Profile Status ────────────────────────────────────────────── */}
      {!payout_profile.configured && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button size="small" onClick={() => navigate('/account/payout-profile')}>
              Set Up Now
            </Button>
          }
        >
          <strong>Payout profile not configured.</strong> You must add your bank account or UPI details before the admin can transfer earned funds to you.
        </Alert>
      )}
      {payout_profile.configured && (
        <Paper
          variant="outlined"
          sx={{
            mb: 3, p: 2,
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2,
            background: 'rgba(16,185,129,0.05)',
            borderColor: payout_profile.verification_status === 'VERIFIED' ? 'success.main'
              : payout_profile.verification_status === 'REJECTED' ? 'error.main' : 'warning.main',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceIcon color={
              payout_profile.verification_status === 'VERIFIED' ? 'success'
              : payout_profile.verification_status === 'REJECTED' ? 'error' : 'warning'
            } />
            <Typography variant="body2" fontWeight={600}>
              Payout Account Configured
            </Typography>
          </Box>
          <Chip
            icon={payout_profile.verification_status === 'VERIFIED' ? <VerifiedIcon /> : <HourglassTopIcon />}
            label={payout_profile.verification_status || 'PENDING'}
            color={
              payout_profile.verification_status === 'VERIFIED' ? 'success'
              : payout_profile.verification_status === 'REJECTED' ? 'error' : 'warning'
            }
            size="small"
          />
          <Typography variant="caption" color="text.secondary">
            IFSC: <strong>{payout_profile.ifsc_code}</strong>
            {payout_profile.upi_id ? ` · UPI: ${payout_profile.upi_id}` : ''}
          </Typography>
          {payout_profile.has_dummy_account && (
            <Chip
              icon={<PaymentIcon />}
              label={`Razorpay Test Account · IFSC: ${payout_profile.dummy_ifsc_code || 'N/A'}`}
              size="small"
              color="secondary"
              variant="outlined"
            />
          )}
        </Paper>
      )}

      {/* ── Summary Cards ────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
        <SummaryCard
          icon={<AccountBalanceWalletIcon />}
          label="Total Earned (Net)"
          value={fmt(summary.total_net)}
          color="#10b981"
          sublabel={`Gross: Rs.${fmt(summary.total_gross)}`}
        />
        <SummaryCard
          icon={<HourglassTopIcon />}
          label="Pending Bank Transfer"
          value={fmt(summary.pending_transfer)}
          color="#f59e0b"
          sublabel="Released but not yet sent to bank"
        />
        <SummaryCard
          icon={<AssignmentTurnedInIcon />}
          label="Total Payouts"
          value={summary.payout_count ?? 0}
          color="#818cf8"
          sublabel="Individual releases"
        />
        <SummaryCard
          icon={<PaymentIcon />}
          label="Platform Fees Deducted"
          value={fmt(summary.total_platform_fee)}
          color="#f43f5e"
          sublabel="5% per payout"
        />
      </Box>

      {/* ── Payout History Table ─────────────────────────────────────────────── */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
        Payout History
      </Typography>

      {payouts.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 6, textAlign: 'center', borderRadius: 3,
            background: 'rgba(15,23,42,0.5)',
            borderColor: 'rgba(129,140,248,0.15)',
          }}
        >
          <EmojiEventsIcon sx={{ fontSize: 56, color: 'rgba(129,140,248,0.3)', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No payouts yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
            Once the admin releases funds for a completed deal, your earnings will appear here.
            Make sure your payout profile is set up.
          </Typography>
          {!payout_profile.configured && (
            <Button
              variant="contained"
              sx={{ mt: 3, borderRadius: 20 }}
              startIcon={<AccountBalanceIcon />}
              onClick={() => navigate('/account/payout-profile')}
            >
              Set Up Payout Profile
            </Button>
          )}
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3, overflow: 'hidden',
            background: 'rgba(15,23,42,0.5)',
            borderColor: 'rgba(129,140,248,0.15)',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  background: 'linear-gradient(90deg, rgba(99,102,241,0.12) 0%, rgba(15,23,42,0.8) 100%)',
                }}
              >
                <TableCell><strong>Deal</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Gross</strong></TableCell>
                <TableCell><strong>Fee (5%)</strong></TableCell>
                <TableCell><strong>Net Payout</strong></TableCell>
                <TableCell><strong>Released At</strong></TableCell>
                <TableCell><strong>Bank Transfer</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payouts.map((p) => {
                const typeConf = PAYOUT_TYPE_CONFIG[p.payout_type] || { label: p.payout_type, color: 'default' };
                const transferred = p.bank_transfer_done;
                return (
                  <TableRow
                    key={p.id}
                    hover
                    sx={{
                      '&:hover': { background: 'rgba(129,140,248,0.04)' },
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                    }}
                  >
                    {/* Deal */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>
                          #{p.deal_id}
                        </Typography>
                        <Tooltip title="View deal">
                          <Box
                            component="span"
                            sx={{ cursor: 'pointer', color: 'primary.main', display: 'flex', alignItems: 'center' }}
                            onClick={() => navigate(`/deals/${p.deal_id}`)}
                          >
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </Box>
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {p.requirement_title}
                      </Typography>
                      {p.milestone_title && (
                        <Typography variant="caption" color="info.main" display="block">
                          Milestone: {p.milestone_title}
                        </Typography>
                      )}
                    </TableCell>

                    {/* Type */}
                    <TableCell>
                      <Chip label={typeConf.label} color={typeConf.color} size="small" />
                    </TableCell>

                    {/* Amounts */}
                    <TableCell>
                      <Typography variant="body2">Rs.{fmt(p.gross_amount)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="error.main">−Rs.{fmt(p.platform_fee)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        Rs.{fmt(p.net_payout)}
                      </Typography>
                    </TableCell>

                    {/* Released At */}
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {fmtDate(p.released_at)}
                      </Typography>
                    </TableCell>

                    {/* Bank Transfer */}
                    <TableCell>
                      {transferred ? (
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                            <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                            <Typography variant="caption" color="success.main" fontWeight={600}>
                              Transferred
                            </Typography>
                          </Box>
                          {p.payout_reference && (
                            <Tooltip title="Razorpay Payout ID / Reference">
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: 'monospace',
                                  color: 'text.secondary',
                                  bgcolor: 'rgba(255,255,255,0.05)',
                                  px: 0.75, py: 0.25, borderRadius: 1,
                                  display: 'inline-block', maxWidth: 160,
                                  overflow: 'hidden', textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {p.payout_reference}
                              </Typography>
                            </Tooltip>
                          )}
                          {p.payout_initiated_at && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {fmtDate(p.payout_initiated_at)}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <HourglassTopIcon color="warning" sx={{ fontSize: 16 }} />
                          <Typography variant="caption" color="warning.main">
                            Awaiting transfer
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Footer summary row */}
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
          <Box
            sx={{
              px: 3, py: 2,
              display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center',
              background: 'rgba(99,102,241,0.05)',
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">TOTAL GROSS</Typography>
              <Typography variant="body1" fontWeight={700}>Rs.{fmt(summary.total_gross)}</Typography>
            </Box>
            <WarningAmberIcon sx={{ color: 'rgba(255,255,255,0.12)', fontSize: 18 }} />
            <Box>
              <Typography variant="caption" color="text.secondary">PLATFORM FEES (5%)</Typography>
              <Typography variant="body1" fontWeight={700} color="error.main">−Rs.{fmt(summary.total_platform_fee)}</Typography>
            </Box>
            <Box sx={{ ml: 'auto' }}>
              <Typography variant="caption" color="text.secondary">NET EARNINGS</Typography>
              <Typography variant="h6" fontWeight={800} color="success.main">Rs.{fmt(summary.total_net)}</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* ── Razorpay Simulation Info Box ─────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{
          mt: 4, p: 2.5, borderRadius: 3,
          background: 'rgba(124,58,237,0.06)',
          borderColor: 'rgba(124,58,237,0.25)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <PaymentIcon sx={{ color: '#7c3aed', fontSize: 22 }} />
          <Typography variant="subtitle2" color="#a78bfa" fontWeight={700}>
            Razorpay Payout Simulation
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          When the admin initiates a Razorpay test payout (using your dummy account), the Bank Transfer column above will update with the Razorpay payout reference ID. This simulates a live bank transfer via Razorpay X and is identical to production — only the dummy account details are used.
        </Typography>
        {!payout_profile.has_dummy_account && payout_profile.configured && (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            You haven't added a <strong>dummy Razorpay test account</strong> yet.
            Go to <strong>Payout Profile → Developer Settings</strong> to add one so the admin can run a simulated Razorpay bank transfer.
            <Button
              size="small"
              sx={{ ml: 1, textTransform: 'none' }}
              onClick={() => navigate('/account/payout-profile')}
            >
              Add Dummy Account
            </Button>
          </Alert>
        )}
        {payout_profile.has_dummy_account && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ borderRadius: 2 }}>
            Dummy Razorpay test account is configured (IFSC: <strong>{payout_profile.dummy_ifsc_code}</strong>).
            The admin can now trigger a simulated Razorpay payout from the Admin → Deal Management panel.
          </Alert>
        )}
        {!payout_profile.configured && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Set up your payout profile first to enable Razorpay simulation.
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
